import { Box, Typography } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-pro";
import pluralize from "pluralize";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useColors from "../../../../../api/hooks/useColors";

import AssigneeLabel from "../../../../../components/assignee/AssigneeLabel";
import CustomToolTip from "../../../../../components/CustomTooltip";
import FavoriteTag from "../../../../../components/FavoriteTag";
import StyledDataGrid from "../../../../../components/StyledDataGrid";
import { CustomerFeatureFlagsContext } from "../../../../../contexts/CustomerFeatureFlagsContext";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { RemediationTypeChip } from "../../../../../utils/chipsData";
import { ControlStatus } from "../../../../../utils/constants";
import AsanaTicket from "../../../../posture/controls/components/ControlRemediationDrawer/control/AsanaTicket";
import ImpactChip from "../../../../posture/controls/components/ControlRemediationDrawer/control/ImpactChip";
import JiraTicket from "../../../../posture/controls/components/ControlRemediationDrawer/control/JiraTicket";
import ServiceNowTicket from "../../../../posture/controls/components/ControlRemediationDrawer/control/ServiceNowTicket";
import ControlStatusIndicator from "../../../../posture/controls/components/ControlStatusIndicator";
import CustomSeveritySelect from "../../../../posture/controls/components/CustomSeveritySelect";
import RecentlyFoundLabel, {
  isRecentlyFound,
} from "../../../../posture/controls/components/RecentlyFoundLabel";
import DetectionLabelIcon from "../../../../posture/detections/components/DetectionLabelIcon";
import DetectionsTooltip from "../../../../posture/detections/components/DetectionsTooltip";
import { getActiveDetections } from "../../../../posture/detections/components/utils";
import ChipColor from "../../../../remediation/plan/components/ChipColor";
import { IntegrationsMapping } from "../../../../stack/integrations/IntegrationsMapping";

export default function ControlsTableWidget({
  controls,
  loading,
  refToPass = undefined,
  columnsToShow = [],
  hideFooter = false,
  sort = [],
}) {
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const [, setSearchParams] = useSearchParams();
  const colors = useColors();
  const [sortModel, setSortModel] = useState([
    {
      field: "status",
      sort: "desc",
    },
    {
      field: "severity",
      sort: "desc",
    },
  ]);

  useEffect(() => {
    if (sort.length > 0) {
      setSortModel(sort);
    }
  }, [sort]);

  const DetectionIcon = ({ row }) => {
    const maxSeverity = row?.last_detections?.length
      ? Math.max(...row?.last_detections.map((detection) => detection.severity))
      : 0;
    return (
      <Box display="flex" alignItems="center">
        <DetectionsTooltip
          content={
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                }}
              >
                <Typography variant="caption" color={colors.common_black}>
                  {getActiveDetections(row?.last_detections).length +
                    pluralize(
                      " Active event",
                      getActiveDetections(row?.last_detections).length
                    ) +
                    "."}
                </Typography>
                <Typography variant="caption">Highest Severity:</Typography>
                <ChipColor
                  value={maxSeverity}
                  type={"severity"}
                  size="small"
                  labelWidth={true}
                />
              </Box>
              <Typography variant="caption">
                Click the control to view details
              </Typography>
            </Box>
          }
        >
          <Box>
            <DetectionLabelIcon sx={{ width: "22px" }} />
          </Box>
        </DetectionsTooltip>
      </Box>
    );
  };

  const columns = [
    { ...GRID_CHECKBOX_SELECTION_COL_DEF, flex: 0.03 },

    {
      field: "id",
      headerName: "ID",
      headerAlign: "left",
      width: 70,
      renderCell: (params) => {
        return <Typography variant="caption">#{params.value}</Typography>;
      },
    },
    {
      field: "name",
      headerName: "Description",
      flex: 0.45,
      minWidth: 250,
      renderCell: (params) => {
        const isFavorite = params.row?.is_favorite;

        return (
          <Box display="flex" flexDirection="row" gap={1}>
            {isFavorite && <FavoriteTag />}
            {detectionsActive && params.row?.is_detected && (
              <DetectionIcon row={params.row} />
            )}
            <Typography variant="caption">{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: "name_for_gap_state",
      headerName: "Description",
      flex: 0.45,
      minWidth: 250,
      renderCell: (params) => {
        const isFavorite = params.row?.is_favorite;
        return (
          <Box
            display="flex"
            flexDirection={"row"}
            gap={1}
            alignItems={"center"}
            sx={{ height: "100%", overflow: "hidden", paddingLeft: "1px" }}
          >
            {detectionsActive && params.row?.is_detected && (
              <DetectionIcon row={params.row} />
            )}
            {isFavorite && <FavoriteTag />}
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              width={"100%"}
              sx={{ minWidth: 0, overflow: "hidden" }}
              gap={1}
            >
              <Typography
                variant="caption"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {params.value}
              </Typography>

              {isRecentlyFound(params.row?.first_seen?.timestamp_utc) && (
                <Box sx={{ flexShrink: 0 }}>
                  <RecentlyFoundLabel
                    foundTimestamp={params.row?.first_seen?.timestamp_utc || ""}
                  />
                </Box>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "tool",
      headerName: "Tool",
      width: 80,
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width={"100%"}
          >
            <CustomToolTip
              title={
                IntegrationsMapping[params.value.toLowerCase()].displayName
              }
            >
              <Box>
                <SvgToIcon
                  IconLogo={
                    IntegrationsMapping[params.value.toLowerCase()].logo
                  }
                  width={40}
                />
              </Box>
            </CustomToolTip>
          </Box>
        );
      },
    },

    {
      field: "severity",
      headerName: "Severity",
      width: 150,
      valueFormatter: (params) => {
        return RemediationTypeChip["controlPriority"][params.value]?.label;
      },
      renderCell: (params) => (
        <CustomSeveritySelect
          row={params.row}
          renderValue={(severity) => (
            <ChipColor
              value={severity}
              type={"controlPriority"}
              disabled={!params.row.is_gap}
            />
          )}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      sortComparator: (v1, v2) => {
        const statusOrder = {
          [ControlStatus.GAP]: 4,
          [ControlStatus.COMPLIANT]: 3,
          [ControlStatus.SNOOZED]: 2,
          [ControlStatus.EXCLUDED]: 1,
        };

        const value1 = statusOrder[v1] || 0;
        const value2 = statusOrder[v2] || 0;

        return value1 - value2;
      },
      renderCell: (params) => {
        return <ControlStatusIndicator status={params.row?.status} />;
      },
    },
    {
      field: "number_of_affected_scenarios",
      headerName: "Number of Scenarios",
      flex: 0.1,
    },
    {
      field: "assignee",
      headerName: "Assignee",
      flex: 0.1,
      valueGetter: (params) => {
        return params.row.assignee?.display_name || params.row.assignee?.email;
      },
      renderCell: (params) => {
        const baseStyles = {
          textTransform: "none",
          paddingLeft: 1,
          paddingRight: 1,
          width: "auto",
          border: `1px solid ${colors.grey_300}`,
          height: "24px",
          borderRadius: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          gap: 1,
          backgroundColor: colors.grey_50,
          color: colors.text_primary,
        };

        const selectedAssignee = params.row.assignee;
        return (
          <Box sx={baseStyles}>
            <AssigneeLabel assignee={selectedAssignee} />
          </Box>
        );
      },
    },

    {
      field: "impacted_included_entities_count",
      headerName: "Affected Entities",
      width: 150,
      headerAlign: "left",
      align: "left",
      disableReorder: true,
      renderCell: (params) => <ImpactChip numberOfEntities={params.value} />,
    },
    {
      field: "ticket",
      headerName: "Ticket",
      width: 80,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => {
        const ticket = params.row.ticket;
        if (!ticket) {
          return "-";
        }
        return (
          ticket?.jira_details?.jira_link ||
          ticket?.asana_details?.task_link ||
          ticket?.servicenow_details?.incident_link
        );
      },
      renderCell: (params) => {
        const ticket = params.row.ticket;
        if (!ticket) {
          return "-";
        }
        return (
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            {ticket?.jira_details?.ticket_key && <JiraTicket ticket={ticket} />}
            {ticket?.asana_details?.task_gid && <AsanaTicket ticket={ticket} />}
            {ticket?.servicenow_details?.incident_number && (
              <ServiceNowTicket ticket={ticket} />
            )}
          </Box>
        );
      },
    },
  ];

  const columnsToRender = columnsToShow?.length
    ? columns.filter((column) => columnsToShow.includes(column.field))
    : columns;
  return (
    <StyledDataGrid
      columns={columnsToRender}
      rows={controls}
      loading={loading}
      apiRef={refToPass}
      hideFooter={hideFooter}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      initialState={{
        columns: {
          columnVisibilityModel: {
            number_of_affected_scenarios: false,
          },
        },
      }}
      onRowClick={(params) => {
        let control_id = params.row.control_id;
        setSearchParams({ control_id: control_id });
      }}
      pageSizeOptionsOverride={[5]}
      boxSXProps={{
        width: "100%",
        border: "none",
        ".MuiDataGrid-root": {
          border: "none",
          boxShadow: "none",
          "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
            {
              paddingLeft: "16px",
            },
          "& .MuiDataGrid-cell:last-of-type, & .MuiDataGrid-columnHeader:last-of-type":
            {
              paddingRight: "16px",
            },
          "& .MuiDataGrid-virtualScroller": {
            minHeight: "270px",
          },
        },
      }}
    />
  );
}
